import { cva } from 'class-variance-authority';
import { motion } from 'framer-motion';
import { ChevronDown, ChevronRight, Search, Circle } from 'lucide-react';
import mergeRefs from 'merge-refs';
import React, {
  createContext,
  forwardRef,
  HTMLAttributes,
  ReactNode,
  useContext,
  useMemo,
  useState
} from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from 'v5/design-system/Button';
import { focusRings } from 'v5/design-system/_common/focusRing';
import { cn } from 'v5/platform/dom/cn';
import { menuPaddingLeft } from 'v5/patterns/sidebar/menuPadding';

export {
  menuPaddingLeft,
  MENU_PADDING_BASE_REM,
  MENU_PADDING_LEVEL_REM
} from 'v5/patterns/sidebar/menuPadding';

const menuRowClass = 'pr-3';

interface MenuCollapsibleContextValue {
  expandOnChildSelect: boolean;
  notifyChildSelect: () => void;
}

const MenuCollapsibleContext =
  createContext<MenuCollapsibleContextValue | null>(null);

export const useMenuCollapsible = () => useContext(MenuCollapsibleContext);

/**
 * Generic Menu Primitives
 * Can be used in sidebars, dropdowns, or any navigation context
 */

export interface MenuSectionProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  title?: string;
}

export const MenuSection = forwardRef<HTMLDivElement, MenuSectionProps>(
  ({ className, title, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('flex flex-col mb-0.5', className)}
        {...props}
      >
        {title && (
          <div className="py-1 text-xs font-semibold text-fg-muted uppercase tracking-wider">
            {title}
          </div>
        )}
        {children}
      </div>
    );
  }
);
MenuSection.displayName = 'MenuSection';

const menuItemVariants = cva(
  [
    'flex items-center gap-3 py-2 rounded text-[13px] cursor-pointer transition-colors w-full border border-transparent',
    menuRowClass,
    'focus-visible:z-10'
  ],
  {
    variants: {
      variant: {
        default:
          'text-fg-default hocus:border-border-muted hocus:bg-bg-subtle/30',
        active:
          'bg-bg-subtle text-fg-default font-medium hocus:border-border-muted',
        muted: 'text-fg-muted hocus:bg-bg-subtle/50'
      }
    },
    defaultVariants: {
      variant: 'default'
    }
  }
);

export interface MenuItemProps
  extends Omit<HTMLAttributes<HTMLAnchorElement>, 'href'> {
  icon?: ReactNode;
  children: ReactNode;
  level?: number;
  isActive?: boolean;
  disabled?: boolean;
  autoSelect?: boolean;
  href?: string;
  textWrapperClassName?: string;
}

export const MenuItem = forwardRef<HTMLAnchorElement, MenuItemProps>(
  (
    {
      className,
      icon,
      children,
      level = 0,
      isActive = false,
      disabled = false,
      autoSelect,
      textWrapperClassName,
      style,
      ...props
    },
    ref
  ) => {
    const variant = disabled ? 'muted' : isActive ? 'active' : 'default';
    const collapsibleContext = useMenuCollapsible();

    React.useEffect(() => {
      if (!isActive) return;
      collapsibleContext?.notifyChildSelect();
    }, [collapsibleContext, isActive]);

    return (
      <a
        tabIndex={disabled ? -1 : 0}
        ref={ref}
        className={cn(
          menuItemVariants({ variant }),
          focusRings.focus.visible,
          className
        )}
        style={{ paddingLeft: menuPaddingLeft(level), ...style }}
        aria-current={isActive ? 'page' : undefined}
        aria-disabled={disabled ? 'true' : undefined}
        data-auto-select={autoSelect ? 'true' : undefined}
        {...props}
      >
        {icon && (
          <span className="inline-flex size-6 shrink-0 items-center justify-center [&_svg]:size-4">
            {icon}
          </span>
        )}
        <span className={cn('flex-1 text-left truncate', textWrapperClassName)}>
          {children}
        </span>
      </a>
    );
  }
);
MenuItem.displayName = 'MenuItem';

export interface MenuCollapsibleProps extends HTMLAttributes<HTMLDivElement> {
  icon?: ReactNode;
  label: string;
  children: ReactNode;
  level?: number;
  defaultOpen?: boolean;
  dataTestId?: string;
  autoClickOnlyChild?: boolean;
  expandOnChildSelect?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const menuCollapsibleTriggerClass = cn(
  'flex items-center gap-3 py-2 rounded text-[13px] w-full border border-transparent',
  menuRowClass,
  'text-fg-default transition-colors',
  'hocus:border-border-muted hocus:bg-bg-subtle/30',
  'focus-visible:z-10',
  focusRings.focus.visible
);

export const MenuCollapsible = forwardRef<HTMLDivElement, MenuCollapsibleProps>(
  (
    {
      className,
      icon,
      label,
      children,
      level = 0,
      defaultOpen = false,
      autoClickOnlyChild = false,
      expandOnChildSelect = false,
      onOpenChange,
      dataTestId,
      ...props
    },
    ref
  ) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const contentRef = React.useRef<HTMLDivElement>(null);
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const hasBeenToggled = React.useRef(false);
    const [shouldHide, setShouldHide] = useState(!defaultOpen);
    const [isAnimating, setIsAnimating] = React.useState(false);
    const [showActiveIndicator, setShowActiveIndicator] = useState(false);

    const syncActiveIndicator = React.useCallback(() => {
      if (isOpen) {
        setShowActiveIndicator(false);
        return;
      }
      setShowActiveIndicator(
        Boolean(contentRef.current?.querySelector('[aria-current="page"]'))
      );
    }, [isOpen]);

    const handleChildSelect = React.useCallback(() => {
      if (!expandOnChildSelect) {
        return;
      }
      setShouldHide(false);
      setIsOpen(true);
    }, [expandOnChildSelect]);

    const contextValue = useMemo<MenuCollapsibleContextValue>(() => {
      return {
        expandOnChildSelect,
        notifyChildSelect: handleChildSelect
      };
    }, [expandOnChildSelect, handleChildSelect]);

    React.useEffect(() => {
      if (defaultOpen) {
        setIsOpen(true);
        setShouldHide(false);
        return;
      }
      const hasActiveChild = contentRef.current?.querySelector(
        '[aria-current="page"]'
      );
      if (hasActiveChild) {
        setIsOpen(true);
        setShouldHide(false);
      }
    }, [defaultOpen, location.pathname, children]);

    React.useEffect(() => {
      const frame = requestAnimationFrame(syncActiveIndicator);
      return () => cancelAnimationFrame(frame);
    }, [location.pathname, syncActiveIndicator, children]);

    React.useEffect(() => {
      onOpenChange?.(isOpen);
    }, [isOpen, onOpenChange]);

    const handleToggle = () => {
      const wasOpen = isOpen;
      hasBeenToggled.current = true;
      setIsAnimating(true);
      setIsOpen(!isOpen);

      if (!wasOpen) {
        setShouldHide(false);
      }

      if (!wasOpen && contentRef.current) {
        requestAnimationFrame(() => {
          const hasActiveChild =
            contentRef.current?.querySelector('[aria-current]');

          if (!hasActiveChild) {
            const findClickableElement = (element: Element): HTMLElement => {
              if (element.hasAttribute('href')) {
                return element as HTMLElement;
              }

              let current = element.parentElement;
              while (current && current !== contentRef.current) {
                if (current.tagName === 'A' && current.hasAttribute('href')) {
                  return current as HTMLElement;
                }
                current = current.parentElement;
              }

              return element as HTMLElement;
            };

            if (autoClickOnlyChild) {
              const clickables = contentRef.current?.querySelectorAll(
                'a[href], button, [role="button"]'
              );
              if (clickables?.length) {
                const target = findClickableElement(clickables[0]);
                target.click();
                return;
              }
            }

            const autoSelectItem = contentRef.current?.querySelector(
              '[data-auto-select="true"]'
            );
            if (autoSelectItem) {
              const target = findClickableElement(autoSelectItem);
              target.click();
            }
          }
        });
      }
    };

    return (
      <div
        ref={mergeRefs(containerRef, ref)}
        className={cn('flex flex-col gap-0.5', className)}
        data-collapsed={!isOpen}
        {...props}
      >
        <button
          type="button"
          data-testid={dataTestId}
          onClick={handleToggle}
          className={menuCollapsibleTriggerClass}
          style={{ paddingLeft: menuPaddingLeft(level) }}
        >
          {icon && (
            <span className="inline-flex size-6 shrink-0 items-center justify-center">
              {icon}
            </span>
          )}
          <span className="flex-1 text-left truncate">{label}</span>
          <div className="flex items-center gap-1">
            <Circle
              className={cn(
                'size-1.5 fill-current text-fg-accent transition-opacity',
                showActiveIndicator ? 'opacity-100' : 'opacity-0'
              )}
            />
            <motion.div
              animate={{ rotate: isOpen ? 0 : 0 }}
              transition={{ duration: 0.2 }}
            >
              {isOpen ? (
                <ChevronDown className="size-4 shrink-0 text-fg-muted" />
              ) : (
                <ChevronRight className="size-4 shrink-0 text-fg-muted" />
              )}
            </motion.div>
          </div>
        </button>
        <MenuCollapsibleContext.Provider value={contextValue}>
          <motion.div
            initial={false}
            animate={{
              height: isOpen ? 'auto' : 0,
              opacity: isOpen ? 1 : 0
            }}
            transition={
              hasBeenToggled.current
                ? { duration: 0.2, ease: 'easeInOut' }
                : { duration: 0 }
            }
            onAnimationComplete={() => {
              if (!isOpen) {
                setShouldHide(true);
              }
              setIsAnimating(false);
              syncActiveIndicator();
            }}
            ref={contentRef}
            className={cn(
              'flex flex-col gap-0.5 isolate',
              isAnimating && 'overflow-hidden'
            )}
            style={{
              display: shouldHide ? 'none' : 'flex'
            }}
          >
            {children}
          </motion.div>
        </MenuCollapsibleContext.Provider>
      </div>
    );
  }
);
MenuCollapsible.displayName = 'MenuCollapsible';

export interface MenuSearchProps extends HTMLAttributes<HTMLButtonElement> {
  placeholder?: string;
}

export const MenuSearch = forwardRef<HTMLButtonElement, MenuSearchProps>(
  ({ className, placeholder = 'Search...', ...props }, ref) => {
    return (
      <Button
        ref={ref}
        variant="secondary"
        size="sm"
        className={cn('w-full justify-start', className)}
        {...props}
      >
        <Search className="size-4 shrink-0" />
        <span className="flex-1 text-left">{placeholder}</span>
      </Button>
    );
  }
);
MenuSearch.displayName = 'MenuSearch';

export interface MenuSeparatorProps extends HTMLAttributes<HTMLDivElement> {
  level?: number;
}

export const MenuSeparator = forwardRef<HTMLDivElement, MenuSeparatorProps>(
  ({ className, level = 0, style, ...props }, ref) => {
    return (
      <div
        ref={ref}
        role="separator"
        className={cn('my-2 mr-2 h-px bg-border-muted', className)}
        style={{ marginLeft: menuPaddingLeft(level), ...style }}
        {...props}
      />
    );
  }
);
MenuSeparator.displayName = 'MenuSeparator';
